import React from 'react';
import RadarChart from './graph_components/RadarChart';
import SelectedActorsScores from './graph_components/ActorScoreBoard';
import WaterUsageChart from './graph_components/WaterUsageChart';
import WaterHistogramChart from './graph_components/WaterHistogramChart';
import DecisionCategoryChart from './graph_components/DecisionCategoryChart';
import erf from 'compute-erf';
import AdvanceButton from '../other_components/AdvanceButton';

/**
 * LE BILAN D'UN ATELIER DISNEY.
 *
 * Coque commune aux écrans à graphiques (`.board-*`, App.css) et bandeau de
 * chiffres en tête, comme le bilan Compte-Gouttes : ce sont les deux mêmes
 * moments de séance.
 *
 * ATTENTION, LE PERCENTILE EST ENCORE CODÉ EN DUR ICI (mu/sigma ci-dessous) :
 * il ne décrit aucune partie réelle. `/game_summary` le calcule sur les séances
 * jouées (game_summary.py) et c'est ce que fait ACGEndPage ; le brancher ici
 * changerait le chiffre annoncé en salle, donc ce n'est pas une retouche de
 * mise en page. La phrase dit « des autres parties », sans effectif, faute de
 * pouvoir en donner un qui se vérifie.
 *
 * RÉGLABLE PAR `content.metadata`, avec les valeurs Disney par défaut — la
 * page `fin-disney` n'en porte aucune et s'affiche comme avant. C'est ce qui
 * sert l'atelier RATP (`fin-ratp`) sans un troisième écran durci :
 *   - `doughnut_scores` : les empreintes de l'organisation, une carte chacune.
 *     La PREMIÈRE donne le chiffre « d'économies d'eau dans votre
 *     organisation ». Le validateur exige celles-ci, et non celles de Disney
 *     (game_validator._custom_page_required_scores).
 *   - `selected_metrics` : les axes du radar (même nom que dans `acg_summary`).
 *   - `organisation_title` : le titre de la section des donuts.
 *   - `background_image_url` : le décor.
 */
const DEFAULT_SELECTED_METRICS = ['economy', 'carbon_economy', 'popularity', 'water_quality', 'biodiversity'];
const DEFAULT_DOUGHNUT_SCORES = ['disney_lieu_waterfootprint', 'disney_usages_waterfootprint'];
const DEFAULT_ORGANISATION_TITLE = 'Votre entreprise';
const DEFAULT_BACKGROUND = 'https://res.cloudinary.com/dc1felxem/image/upload/v1740671404/hydros90_22124_Sustainable_residential_building_with_eco-frie_0f7eaff6-5480-430a-93d2-4bd13a94ba60_0_ph8ncb.jpg';

const MU = 95.15;
const SIGMA = 18.22;

const cumulativeNormal = (x, mu, sigma) => 0.5 * (1 + erf((x - mu) / (Math.sqrt(2) * sigma)));

const DisneyEndPage = ({ pageData, gameStatus, onDecisionSelect,
                         displayButton = true, endsGame = false }) => {
  const scores = gameStatus.score_dic;
  const selectedActors = gameStatus.list_selected_actors;

  const settings = pageData?.content?.metadata || {};
  const selectedMetrics = settings.selected_metrics?.length ? settings.selected_metrics : DEFAULT_SELECTED_METRICS;
  const doughnutScores = settings.doughnut_scores?.length ? settings.doughnut_scores : DEFAULT_DOUGHNUT_SCORES;
  const organisationTitle = settings.organisation_title || DEFAULT_ORGANISATION_TITLE;
  const background = settings.background_image_url || DEFAULT_BACKGROUND;

  if (!scores) {
    return <div className="score-board-loading">Chargement des scores…</div>;
  }

  const objectifReached = Math.round(100 * scores?.water_projection?.value / (gameStatus.nb_rounds_cg * 20));
  const ratioWorseGames = Math.round(100 * cumulativeNormal(objectifReached, MU, SIGMA));

  const actorScores = selectedActors
    .map((actor) => scores[actor])
    .filter(Boolean)
    .sort((a, b) => b.value - a.value);
  const worstPlayer = actorScores[actorScores.length - 1];

  const organisationSaving = Math.round(100 * (1 - (
    scores[doughnutScores[0]].value / scores[doughnutScores[0]].standard_value)));

  return (
    <div className="page-general-container">
      <div className='page-general-background-color'></div>

      <div className='page-general-background-hero' style={{ backgroundImage: `url(/img/bgrille-m.svg)` }}>
          <div className='page-general-background-image' style={{ backgroundImage: `url(${background})` }}></div>
      </div>

      <div className='page-main-content'>
        <div className="board-page">
          <header className="board-head">
            <h1 className="board-title">{pageData?.title || "Le bilan de l'atelier"}</h1>
          </header>

          {/* Les chiffres de la séance dans un seul bandeau, à la place des
              trois encadrés dispersés en bas de la première section. */}
          <section className="board-hero">
            <div className="board-hero-body">
              <div className="board-figures">
                <div className="board-figure">
                  <div className="board-figure-value">{objectifReached}%</div>
                  <div className="board-figure-label">de votre objectif atteint</div>
                </div>
                <div className="board-figure">
                  <div className="board-figure-value">{organisationSaving}%</div>
                  <div className="board-figure-label">d'économies d'eau dans votre organisation</div>
                </div>
                {worstPlayer && (
                  <div className="board-figure">
                    <div className="board-figure-value board-figure-value--name">{worstPlayer.name}</div>
                    <div className="board-figure-label">a fait le plus d'effort</div>
                  </div>
                )}
              </div>
            </div>
            <p className="board-note">
              Cette séance fait mieux que <strong>{ratioWorseGames}%</strong> des autres parties.
            </p>
          </section>

          <h2 className="board-section">Le territoire</h2>
          <div className="board-grid">
            <section className="board-card board-card--wide board-card--titled">
              <h3 className="board-card-title">Projection des économies d'eau</h3>
              <p className="board-card-hint">Les économies cumulées tour après tour, empilées par secteur d'usage.</p>
              <WaterHistogramChart gameStatus={gameStatus} />
            </section>

            <section className="board-card board-card--titled">
              <h3 className="board-card-title">Les indicateurs du territoire</h3>
              <RadarChart gameStatus={gameStatus} selectedMetrics={selectedMetrics}
                          width="100%" height="300px" />
            </section>

            <section className="board-card board-card--titled">
              <h3 className="board-card-title">Les familles de leviers</h3>
              <DecisionCategoryChart gameStatus={gameStatus} width="100%" height="260px" />
            </section>

            <section className="board-card board-card--wide">
              <SelectedActorsScores gameStatus={gameStatus} podium={true} />
            </section>
          </div>

          <h2 className="board-section">{organisationTitle}</h2>
          <div className="board-grid">
            {doughnutScores.map((scoreId) => {
              const scoreData = scores[scoreId];
              return (
                <section key={scoreId} className="board-card">
                  <WaterUsageChart
                    data={scoreData.metadata.current}
                    unit="m³"
                    totalInitConsumption={scoreData.standard_value}
                    showLegend={false}
                    title={scoreData.name}
                  />
                </section>
              );
            })}
          </div>

          <div className="board-actions">
            <AdvanceButton
              pageData={pageData}
              onDecisionSelect={onDecisionSelect}
              displayButton={displayButton}
              endsGame={endsGame}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisneyEndPage;
